import { DEFAULT_PER_PAGE } from "@/features/admin/components";
import type { ProductListQuery, ProductStatus } from "@nh/shared";
import type { AdminProductsQuery } from "./products.api";

export type ProductSort = ProductListQuery["sort"];

/**
 * Lo que se ve en la barra de filtros.
 *
 * `""` y no `undefined` para "sin filtro": un `<select>` controlado con
 * `value={undefined}` se vuelve NO controlado a mitad de vida, React
 * avisa por consola y el control deja de responder al botón de limpiar.
 * La traducción de `""` a "no mandes este parámetro" ocurre una sola vez,
 * en `toQuery`.
 */
export interface ProductFilters {
  search: string;
  status: ProductStatus | "";
  category: string;
  sort: ProductSort;
  page: number;
  perPage: number;
}

export const DEFAULT_FILTERS: ProductFilters = {
  search: "",
  status: "",
  category: "",
  sort: "recommended",
  page: 1,
  // Cinco por defecto, como TODA tabla del sistema (Anahí, 29-ago).
  perPage: DEFAULT_PER_PAGE,
};

export function toQuery(filters: ProductFilters): AdminProductsQuery {
  return {
    ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
    ...(filters.category ? { category: filters.category } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    page: filters.page,
    perPage: filters.perPage,
    sort: filters.sort,
  };
}

export function hasActiveFilters(filters: ProductFilters): boolean {
  return Boolean(filters.search.trim() || filters.status || filters.category);
}

/**
 * Cambiar cualquier filtro vuelve a la página 1.
 *
 * Sin esto, quien está en la página 4 y escribe una búsqueda con tres
 * resultados ve una tabla vacía y concluye que no hay nada — cuando lo
 * que pasó es que pidió la cuarta página de una lista de una.
 */
export function applyFilterChange(
  current: ProductFilters,
  patch: Partial<ProductFilters>,
): ProductFilters {
  const next = { ...current, ...patch };
  const changedFilter =
    patch.search !== undefined ||
    patch.status !== undefined ||
    patch.category !== undefined ||
    patch.sort !== undefined;
  return changedFilter && patch.page === undefined ? { ...next, page: 1 } : next;
}
